import facebook from '@assets/icons/facebook.svg';
import linkedin from '@assets/icons/linkedin.svg';
import x from '@assets/icons/x.svg';
import { ErrorMessage, TaInput } from '@components/shared';
import { NotificationSnackbar } from '@components/shared/notificationSnackbar';
import { MuiPhone } from '@components/shared/PhoneInput/PhoneInput';
import { TaModal } from '@components/shared/TaModal/TaModal';
import { EditableCard } from '@features/dashboard/specialistDashboard/editableCard/editableCard';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import { Chip, IconButton } from '@mui/material';
import { validatePhoneNumberByCountryCode } from '@utils/phoneValidation';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateUser, useUsersStore } from 'services/users';
import * as Yup from 'yup';

const initialValues = {
	phone: '',
	linkedin: '',
	facebook: '',
	x: '',
};

export const ContactDetails = ({ readonly = false }: { readonly?: boolean }) => {
	const { t } = useTranslation();
	const mutate = useUpdateUser();
	const [open, setOpen] = useState(false);
	const [snackbarOpen, setSnackbarOpen] = useState(false);
	const { user } = useUsersStore();
	const [isLoading, setIsLoading] = useState(false);
	const [phoneValidationError, setPhoneValidationError] = useState<string>('');

	const contactDetailsValidationSchema = Yup.object({
		linkedin: Yup.string()
			.test('is-linkedin', 'mustBeLinkedInUrl', (value) => {
				if (!value) return true; // Allow empty value

				return value.includes('linkedin.com');
			})
			.url('invalidUrl'),
		facebook: Yup.string()
			.test('is-linkedin', 'mustBeFacebookUrl', (value) => {
				if (!value) return true; // Allow empty value

				return value.includes('facebook.com');
			})
			.url('invalidUrl'),
		phone: Yup.string()
			.required('fieldNameError')
			.test('phone-validation', t('phoneNumberMatchError'), function (value) {
				if (!value) return false;

				const validation = validatePhoneNumberByCountryCode(value, t);
				if (!validation.isValid) {
					return this.createError({ message: validation.errorMessage });
				}
				return true;
			}),
		x: Yup.string()
			.test('is-linkedin', 'mustBeXUrl', (value) => {
				if (!value) return true; // Allow empty value

				return value.includes('x.com');
			})
			.url('invalidUrl'),
	});

	const formik = useFormik({
		initialValues: initialValues,
		validationSchema: contactDetailsValidationSchema,
		onSubmit: async (values) => {
			try {
				setIsLoading(true);

				// Final validation before submission
				const phoneValidation = validatePhoneNumberByCountryCode(values.phone, t);
				if (!phoneValidation.isValid) {
					setPhoneValidationError(phoneValidation.errorMessage || t('phoneNumberMatchError'));
					setIsLoading(false);
					return;
				}

				const socialLinks = [
					values.linkedin && { linkedin: values.linkedin },
					values.facebook && { facebook: values.facebook },
					values.x && { x: values.x },
				].filter(Boolean);

				await mutate({
					phone: values.phone,
					socialLinks: socialLinks as any,
				});
				setOpen(false);
				setSnackbarOpen(true);
			} catch (error) {
				console.error('Update failed:', error);
			} finally {
				setIsLoading(false);
			}
		},
	});

	const { setValues } = formik;

	// Handle phone number change with real-time validation
	const handlePhoneChange = (phone: string) => {
		formik.setFieldValue('phone', phone);
		setPhoneValidationError('');

		// Real-time validation based on phone number's country code
		if (phone) {
			const validation = validatePhoneNumberByCountryCode(phone, t);
			if (!validation.isValid) {
				setPhoneValidationError(validation.errorMessage || t('phoneNumberMatchError'));
			}
		}
	};

	useEffect(() => {
		if (user?.social_link) {
			setValues({
				phone: user?.phone || '',
				linkedin: user?.social_link?.link?.linkedin || '',
				facebook: user?.social_link?.link?.facebook || '',
				x: user?.social_link?.link?.x || '',
			});
		}
	}, [setValues, user?.phone, user?.social_link]);

	return (
		<EditableCard
			onEdit={() => setOpen(true)}
			className="responsive-card"
			title={t('contactDetails')}
			readonly={readonly}
		>
			<div className="flex flex-col gap-[20px] pb-5">
				<div className="flex gap-[10px]">
					<EmailOutlinedIcon className="text-secondary" />
					<div className="w-[1px] h-[20px] bg-[#E9ECEF]"></div>
					<p className="text-[#343A40] roboto-regular text-[16px] leading-[140%]">{user?.email}</p>
					<Chip
						label={t('private')}
						variant="outlined"
						sx={{
							borderRadius: '5px',
							border: '1px solid #CED4DA',
							backgroundColor: '#F8F9FA',
							padding: '1px 5px',
							color: '#7B809A',
							fontFamily: 'Roboto',
							fontSize: '11px',
							fontStyle: 'normal',
							fontWeight: 400,
							lineHeight: '140%',
							height: '15px',
							textTransform: 'camelcase',
							'& .MuiChip-label': {
								padding: 0,
							},
						}}
					/>
				</div>
				<div className="flex gap-[10px]">
					<LocalPhoneOutlinedIcon className="text-secondary" />
					<div className="w-[1px] h-[20px] bg-[#E9ECEF]"></div>
					<p className="text-[#343A40] roboto-regular text-[16px] leading-[140%]">{user?.phone}</p>
					<Chip
						label={t('private')}
						variant="outlined"
						sx={{
							borderRadius: '5px',
							border: '1px solid #CED4DA',
							backgroundColor: '#F8F9FA',
							padding: '1px 5px',
							color: '#7B809A',
							fontFamily: 'Roboto',
							fontSize: '11px',
							fontStyle: 'normal',
							fontWeight: 400,
							lineHeight: '140%',
							height: '15px',
							textTransform: 'camelcase',
							'& .MuiChip-label': {
								padding: 0,
							},
						}}
					/>
				</div>
				<div className="flex gap-[10px] items-center">
					<PublicOutlinedIcon className="text-secondary" />
					<div className="w-[1px] h-[20px] bg-[#E9ECEF]"></div>
					<div className="flex ">
						<IconButton href={user?.social_link?.link?.linkedin} target="_blank">
							<div className="flex p-[5px] items-center gap-[10px] rounded-[10px] bg-[#F8F9FA]">
								<img src={linkedin} alt="linkedin" />
							</div>
						</IconButton>
						<IconButton href={user?.social_link?.link?.facebook} target="_blank">
							<div className="flex p-[5px] items-center gap-[10px] rounded-[10px] bg-[#F8F9FA]">
								<img src={facebook} alt="facebook" />
							</div>
						</IconButton>
						<IconButton href={user?.social_link?.link?.x} target="_blank">
							<div className="flex p-[5px] items-center gap-[10px] rounded-[10px] bg-[#F8F9FA]">
								<img src={x} alt="x" />
							</div>
						</IconButton>
					</div>
				</div>
			</div>
			<TaModal
				save={formik.handleSubmit}
				open={open}
				saveButtonLoading={isLoading}
				disableSaveButton={isLoading}
				onClose={() => setOpen(false)}
				title={t('contactDetails')}
				showActions
			>
				<div className="flex flex-col gap-[10px] mt-[20px]">
					<span className="roboto-regular text-[16px] leading-[140%]">{t('onlyTeam')}</span>
					<MuiPhone
						className="w-full"
						name="phone"
						placeholder={t('phoneNumber')}
						value={formik.values.phone}
						onChange={handlePhoneChange}
						onBlur={formik.handleBlur}
						error={
							(formik.touched.phone && Boolean(formik.errors.phone)) ||
							Boolean(phoneValidationError)
						}
					/>
					{((formik.touched.phone && formik.errors.phone) || phoneValidationError) && (
						<ErrorMessage
							message={
								phoneValidationError ||
								t(formik.errors.phone || '', {
									fieldName: t('phoneNumber'),
								})
							}
						/>
					)}
					<div>
						<TaInput
							icon={<img src={linkedin} alt="linkedin" />}
							id="linkedin"
							label={t('linkedInProfile')}
							size="small"
							isRequired
							value={formik.values.linkedin}
							onChange={formik.handleChange}
							error={formik.touched.linkedin && Boolean(formik.errors.linkedin)}
						/>
						{formik.touched.linkedin && formik.errors.linkedin && (
							<ErrorMessage
								message={t(formik.errors.linkedin || '', {
									fieldName: t('linkedInProfile'),
								})}
							/>
						)}
					</div>
					<div>
						<TaInput
							icon={<img src={facebook} alt="facebook" />}
							id="facebook"
							label={t('facebookProfile')}
							size="small"
							value={formik.values.facebook}
							onChange={formik.handleChange}
							error={formik.touched.facebook && Boolean(formik.errors.facebook)}
						/>
						{formik.touched.facebook && formik.errors.facebook && (
							<ErrorMessage
								message={t(formik.errors.facebook || '', {
									fieldName: t('facebookProfile'),
								})}
							/>
						)}
					</div>
					<div>
						<TaInput
							icon={<img src={x} alt="x" />}
							id="x"
							label={t('xProfile')}
							size="small"
							value={formik.values.x}
							onChange={formik.handleChange}
							error={formik.touched.x && Boolean(formik.errors.x)}
						/>
						{formik.touched.x && formik.errors.x && (
							<ErrorMessage
								message={t(formik.errors.x || '', {
									fieldName: t('xProfile'),
								})}
							/>
						)}
					</div>
				</div>
			</TaModal>
			<NotificationSnackbar
				open={snackbarOpen}
				onClose={() => setSnackbarOpen(false)}
				title={t('successfullyUpdated')}
				success
				description={t('profileUpdated', {
					title: t('contactDetails'),
				})}
			/>
		</EditableCard>
	);
};
